import { useFirestoreGeneral } from '../../firebase/useFirestore'
import useShowWhenLabels from '../../hooks/useShowWhenLabels'
import describeShowWhen from '../../helpers/questionnaire/describeShowWhen'
import { useTranslation } from '../../StateManagment/TranslationContext'

// De regel onder een conditionele vraag op het printvel.
//
// Papier vertakt niet: een vraag die digitaal alleen na een bepaald antwoord
// verschijnt, staat op het vel gewoon tussen de rest. Zonder deze zin vult de
// invuller een vraag in die hij op het scherm nooit gekregen zou hebben — de
// papieren-enquêteconventie is om de voorwaarde erbij te zetten.
//
// De zin zelf komt uit `describeShowWhen`, dezelfde bron als de chip in de
// bouwer en de regel onder een grafiek; alleen de aanhef is van papier.
// AI-CONTEXT: See docs/features/conditional-questions.md
//
// De opzoeking gaat tenant-gescoped: dit scherm toont één vragenlijst van één
// omgeving, en heeft de cross-tenant variant die de analysekant gebruikt niet
// nodig.
// AI-CONTEXT: See docs/pitfalls.md#database-missing-compagnyid-on-fresh-fetch

/**
 * @param {object} props
 * @param {object} props.field - Het QuestionnaireFields-doc met (mogelijk) een `ShowWhen`
 */
const ShowWhenNote = ({ field }) => {
    const { triggerField, labels, loaded } = useShowWhenLabels(field, { queryHook: useFirestoreGeneral })
    const { translations } = useTranslation()

    if (!field?.ShowWhen?.FieldID) return null

    // Een nog ladende opzoeking ziet er precies zo uit als een verwijderde
    // triggervraag, en een zin zonder de antwoordtekst zegt iets anders ('is
    // beantwoord') dan de regel werkelijk doet. Eerst zekerheid, dan pas een
    // bewering aan de lezer.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    if (!loaded) return null

    // Fail-open: zonder triggervraag stelt het invulscherm de vraag aan iedereen,
    // dus dat is wat er op papier hoort te staan. De reden erbij zetten helpt de
    // invuller niet — die kan er niets mee.
    const t = (text) => translations?.[text] || text

    if (!triggerField) {
        return (
            <p className="print-questionaire-showwhen-note">
                {t('Deze vraag wordt altijd gesteld.')}
            </p>
        )
    }

    // De vraagtekst en de antwoordteksten gaan vertaald de zin in; de
    // voegwoorden die `describeShowWhen` zelf legt ('of', 'is beantwoord')
    // blijven Nederlands — zie docs/inbox.md #187.
    //
    // LET OP DE SLEUTEL: een vraag staat onder zijn veld-ID in de vertaalset,
    // een antwoordtekst onder zichzelf. Zoek je de vraag op met haar tekst, dan
    // blijft juist die Nederlands terwijl de rest van de zin vertaald is.
    const translatedTrigger = {
        ...triggerField,
        Question: translations?.[triggerField.ID] || triggerField.Question,
    }
    const translatedLabels = Object.fromEntries(
        Object.entries(labels).map(([id, text]) => [id, t(text)])
    )

    const { state, condition } = describeShowWhen(field, [translatedTrigger], translatedLabels)
    if (state !== 'ok') return null

    return <p className="print-questionaire-showwhen-note">{t('Alleen invullen als')}: {condition}</p>
}

export default ShowWhenNote
